import { useEffect, useRef, useCallback } from "react";
import type { MediaPlayerInstance } from "@vidstack/react";
import { useOs } from "@mantine/hooks";

interface MediaSessionMetadata {
	title: string;
	artist?: string;
	album?: string;
	artwork?: Array<{ src: string; sizes?: string; type?: string }>;
}

export const useNativeMediaSession = (
	player: React.RefObject<MediaPlayerInstance>,
	metadata: MediaSessionMetadata,
	isActive: boolean
): {
	updatePlaybackState: (state: "playing" | "paused" | "none") => void;
} => {
	const cleanupRef = useRef<(() => void) | null>(null);
	const positionIntervalRef = useRef<number>();
	const os = useOs();
	const isAndroid = os === "android";
	const isIOS = os === "ios";

	const setupMediaSession = useCallback(
		(mediaElement: HTMLVideoElement | HTMLAudioElement) => {
			if (!navigator.mediaSession) {
				console.warn("MediaSession API not available");
				return null;
			}

			console.log("🔧 Setting up native Media Session");

			// Set metadata
			try {
				navigator.mediaSession.metadata = new MediaMetadata({
					title: metadata.title,
					artist: metadata.artist || "Akademia LTCA",
					album: metadata.album || "Kursy LTCA",
					artwork: metadata.artwork || [
						{ src: "/assets/logo-only.png", sizes: "512x512", type: "image/png" },
					],
				});
				console.log("✅ Media Session metadata set");
			} catch (error) {
				console.warn("Failed to set metadata:", error);
			}

			// Update position periodically
			const updatePositionState = (): void => {
				if (!mediaElement) return;

				try {
					const duration = mediaElement.duration || 0;
					const position = mediaElement.currentTime || 0;
					const playbackRate = mediaElement.playbackRate || 1;

					if (duration > 0) {
						navigator.mediaSession?.setPositionState?.({
							duration,
							position,
							playbackRate,
						});
					}
				} catch (error) {
					console.warn("Failed to update position state:", error);
				}
			};

			// Action handlers
			try {
				navigator.mediaSession.setActionHandler("play", () => {
					console.log("🎵 Native Media Session: Play");
					mediaElement
						.play()
						.then(() => {
							navigator.mediaSession.playbackState = "playing";
						})
						.catch((error) => {
							console.error("Play failed:", error);
						});
				});

				navigator.mediaSession.setActionHandler("pause", () => {
					console.log("⏸️ Native Media Session: Pause");
					mediaElement.pause();
					navigator.mediaSession.playbackState = "paused";
				});

				// Different seek times for different platforms
				const seekTime = isAndroid ? 15 : 10;

				navigator.mediaSession.setActionHandler("seekbackward", (details) => {
					const offset = details.seekOffset || seekTime;
					mediaElement.currentTime = Math.max(
						0,
						mediaElement.currentTime - offset
					);
					console.log(`⏪ Seek backward ${offset}s`);
					updatePositionState();
				});

				navigator.mediaSession.setActionHandler("seekforward", (details) => {
					const offset = details.seekOffset || seekTime;
					const duration = mediaElement.duration || 0;
					mediaElement.currentTime = Math.min(
						duration,
						mediaElement.currentTime + offset
					);
					console.log(`⏩ Seek forward ${offset}s`);
					updatePositionState();
				});

				navigator.mediaSession.setActionHandler("seekto", (details) => {
					if (details.seekTime != null) {
						mediaElement.currentTime = details.seekTime;
						console.log(`🎯 Seek to ${details.seekTime}s`);
						updatePositionState();
					}
				});

				// Android-specific stop handler
				if (isAndroid) {
					navigator.mediaSession.setActionHandler("stop", () => {
						console.log("⏹️ Native Media Session: Stop");
						mediaElement.pause();
						navigator.mediaSession.playbackState = "none";
					});
				}

				console.log("✅ Native Media Session handlers set");
			} catch (error) {
				console.warn("Failed to set action handlers:", error);
			}

			// More frequent updates on Android
			const updateInterval = isAndroid ? 3000 : 5000;
			positionIntervalRef.current = window.setInterval(
				updatePositionState,
				updateInterval
			);
			updatePositionState(); // Initial update

			// iOS-specific: Handle visibility changes
			let visibilityHandler: (() => void) | undefined;
			if (isIOS) {
				visibilityHandler = (): void => {
					if (document.hidden && !mediaElement.paused) {
						console.log(
							"🍎 iOS: Video going to background, maintaining session"
						);
						navigator.mediaSession.playbackState = "playing";
						updatePositionState();
					}
				};
				document.addEventListener("visibilitychange", visibilityHandler);
			}

			// Return cleanup function
			return (): void => {
				console.log("🧹 Cleaning up native Media Session");

				if (positionIntervalRef.current) {
					clearInterval(positionIntervalRef.current);
				}

				if (visibilityHandler) {
					document.removeEventListener("visibilitychange", visibilityHandler);
				}

				try {
					navigator.mediaSession?.setActionHandler("play", null);
					navigator.mediaSession?.setActionHandler("pause", null);
					navigator.mediaSession?.setActionHandler("seekbackward", null);
					navigator.mediaSession?.setActionHandler("seekforward", null);
					navigator.mediaSession?.setActionHandler("seekto", null);

					if (isAndroid) {
						navigator.mediaSession?.setActionHandler("stop", null);
					}

					if (navigator.mediaSession) {
						navigator.mediaSession.metadata = null;
						navigator.mediaSession.playbackState = "none";
					}
				} catch (error) {
					console.warn("Cleanup failed:", error);
				}
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[metadata.title, metadata.artist, metadata.album, isAndroid, isIOS]
	);

	useEffect(() => {
		if (!isActive || !player.current) {
			// Cleanup if not active
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
			return;
		}

		// Wait for player to be ready
		const setupTimer = setTimeout(() => {
			const playerElement = player.current?.el;
			if (playerElement) {
				// Find the actual video/audio element
				const mediaElement = playerElement.querySelector("video, audio") as
					| HTMLVideoElement
					| HTMLAudioElement;

				if (mediaElement) {
					console.log("🎬 Setting up native Media Session for player");
					cleanupRef.current = setupMediaSession(mediaElement);
				} else {
					console.warn("❌ Could not find media element in player");
				}
			}
		}, 1000); // Give time for player to load

		return (): void => {
			clearTimeout(setupTimer);
			if (cleanupRef.current) {
				cleanupRef.current();
				cleanupRef.current = null;
			}
		};
	}, [isActive, setupMediaSession, player]);

	// Helper function to update playback state
	const updatePlaybackState = useCallback(
		(state: "playing" | "paused" | "none"): void => {
			try {
				if (navigator.mediaSession) {
					navigator.mediaSession.playbackState = state;
					console.log(`🎵 Playback state: ${state}`);

					// Force position update on state change (especially for Android)
					if (isAndroid && player.current) {
						setTimeout(() => {
							const playerElement = player.current?.el;
							const mediaElement = playerElement?.querySelector(
								"video, audio"
							) as HTMLVideoElement | HTMLAudioElement;

							if (mediaElement) {
								try {
									const duration = mediaElement.duration || 0;
									const position = mediaElement.currentTime || 0;
									const playbackRate = mediaElement.playbackRate || 1;

									if (duration > 0) {
										navigator.mediaSession?.setPositionState?.({
											duration,
											position,
											playbackRate,
										});
									}
								} catch (error) {
									console.warn("Failed to update position state:", error);
								}
							}
						}, 100);
					}
				}
			} catch (error) {
				console.warn(`Failed to update playback state to ${state}:`, error);
			}
		},
		[isAndroid, player]
	);

	return { updatePlaybackState };
};
